// src/web/client/src/components/analytics/ABTesting.jsx
// A/B Testing Dashboard Component - Pro Feature

import React, { useState, useEffect } from 'react';
import { safeApi, isFeatureError } from '@/utils/api';

export default function ABTesting({ compact = false }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tests, setTests] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);

  useEffect(() => {
    fetchTests();
  }, []);

  const fetchTests = async () => {
    setLoading(true);
    try {
      const data = await safeApi.get('/ab-tests');
      
      if (isFeatureError(data)) {
        setError(data);
        return;
      }
      
      setTests(data.data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch tests:', err);
      setError({ message: 'Failed to load tests' });
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = async (testId) => {
    try {
      await safeApi.post(`/ab-tests/${testId}/start`);
      fetchTests();
    } catch (err) {
      console.error('Failed to start test:', err);
    }
  };

  const handlePauseTest = async (testId) => {
    try {
      await safeApi.post(`/ab-tests/${testId}/pause`);
      fetchTests();
    } catch (err) {
      console.error('Failed to pause test:', err);
    }
  };

  const handleEndTest = async (testId) => {
    try {
      await safeApi.post(`/ab-tests/${testId}/end`);
      fetchTests();
    } catch (err) {
      console.error('Failed to end test:', err);
    }
  };

  const handleDeleteTest = async (testId) => {
    if (!confirm('Are you sure you want to delete this test?')) return;
    try {
      await safeApi.delete(`/ab-tests/${testId}`);
      fetchTests();
    } catch (err) {
      console.error('Failed to delete test:', err);
    }
  };

  // Pro Gate
  if (error && error.code === 'FEATURE_NOT_AVAILABLE') {
    return <ProGateMessage />;
  }

  if (loading) {
    return <LoadingState />;
  }

  if (compact) {
    return <CompactView tests={tests} onViewDetails={setSelectedTest} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">🧪 A/B Testing</h2>
            <p className="text-gray-500 mt-1">Test different message variants to optimize performance</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            + New Test
          </button>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-4 gap-4 mt-6">
          <StatCard 
            label="Active Tests" 
            value={tests.filter(t => t.status === 'running').length}
            icon="🔬"
          />
          <StatCard 
            label="Completed" 
            value={tests.filter(t => t.status === 'completed').length}
            icon="✅"
          />
          <StatCard 
            label="Total Messages Tested" 
            value={tests.reduce((sum, t) => sum + (t.total_sent || 0), 0)}
            icon="📧"
          />
          <StatCard 
            label="Winners Found" 
            value={tests.filter(t => t.winner).length}
            icon="🏆"
          />
        </div>
      </div>

      {/* Tests List */}
      {tests.length > 0 ? (
        <div className="space-y-4">
          {tests.map(test => (
            <TestCard
              key={test.test_id}
              test={test}
              onStart={() => handleStartTest(test.test_id)}
              onPause={() => handlePauseTest(test.test_id)}
              onEnd={() => handleEndTest(test.test_id)}
              onDelete={() => handleDeleteTest(test.test_id)}
              onViewDetails={() => setSelectedTest(test)}
            />
          ))}
        </div>
      ) : (
        <EmptyState onCreateTest={() => setShowCreateModal(true)} />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateTestModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchTests();
          }}
        />
      )}

      {/* Test Details Modal */}
      {selectedTest && (
        <TestDetailsModal
          test={selectedTest}
          onClose={() => setSelectedTest(null)}
        />
      )}
    </div>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

function CompactView({ tests, onViewDetails }) {
  const activeTests = tests.filter(t => t.status === 'running');
  
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900">🧪 A/B Tests</h3>
        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">PRO</span>
      </div>
      
      {activeTests.length > 0 ? (
        <div className="space-y-3">
          {activeTests.slice(0, 3).map(test => (
            <div 
              key={test.test_id}
              className="p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
              onClick={() => onViewDetails(test)}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{test.name}</span>
                <span className="text-xs text-green-600">Running</span>
              </div>
              <div className="mt-2">
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div 
                    className="bg-purple-500 h-1.5 rounded-full"
                    style={{ width: `${test.progress || 0}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">{test.progress || 0}% complete</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500 text-center py-4">
          No active tests
        </p>
      )}
    </div>
  );
}

function TestCard({ test, onStart, onPause, onEnd, onDelete, onViewDetails }) {
  const statusColors = {
    draft: 'bg-gray-100 text-gray-700',
    running: 'bg-green-100 text-green-700',
    paused: 'bg-yellow-100 text-yellow-700',
    completed: 'bg-blue-100 text-blue-700'
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-gray-900">{test.name}</h3>
            <span className={`text-xs px-2 py-1 rounded capitalize ${statusColors[test.status]}`}>
              {test.status}
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {test.message_type?.replace(/_/g, ' ')} • {test.variants?.length || 0} variants
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {test.status === 'draft' && (
            <button onClick={onStart} className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200">
              Start
            </button>
          )}
          {test.status === 'running' && (
            <>
              <button onClick={onPause} className="px-3 py-1 text-sm bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200">
                Pause
              </button>
              <button onClick={onEnd} className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
                End
              </button>
            </>
          )}
          {test.status === 'paused' && (
            <button onClick={onStart} className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200">
              Resume
            </button>
          )}
          <button onClick={onViewDetails} className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
            Details
          </button>
          {test.status !== 'running' && (
            <button onClick={onDelete} className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200">
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      {test.status === 'running' && (
        <div className="mt-4">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-gray-500">Progress</span>
            <span className="text-gray-700">{test.total_sent || 0} / {test.min_sample_size} messages</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-purple-500 h-2 rounded-full transition-all"
              style={{ width: `${test.progress || 0}%` }}
            />
          </div>
        </div>
      )}

      {/* Variants Preview */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
        {test.variants?.map(variant => (
          <div 
            key={variant.variant_id}
            className={`p-3 rounded-lg border ${
              test.winner?.variant_id === variant.variant_id 
                ? 'border-green-500 bg-green-50' 
                : 'border-gray-200'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-sm">{variant.name}</span>
              {test.winner?.variant_id === variant.variant_id && (
                <span className="text-xs">🏆</span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <p className="text-gray-500">Sent</p>
                <p className="font-medium">{variant.metrics?.sent || 0}</p>
              </div>
              <div>
                <p className="text-gray-500">Open Rate</p>
                <p className="font-medium">{variant.metrics?.open_rate || 0}%</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Significance */}
      {test.statistical_significance && (
        <div className={`mt-4 p-3 rounded-lg ${
          test.statistical_significance.is_significant 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-gray-50 border border-gray-200'
        }`}>
          <p className="text-sm">
            {test.statistical_significance.message}
          </p>
        </div>
      )}
    </div>
  );
}

function CreateTestModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [messageType, setMessageType] = useState('thank_you');
  // click_rate, not open_rate: opens are Apple-MPP-inflated — the app's
  // standing decision (#135-#139). Matches the backend schema default.
  const [successMetric, setSuccessMetric] = useState('click_rate');
  const [minSampleSize, setMinSampleSize] = useState(100);
  const [variants, setVariants] = useState([
    { name: 'Variant A', subject: '', content: '' },
    { name: 'Variant B', subject: '', content: '' }
  ]);
  const [creating, setCreating] = useState(false);

  const handleSubmit = async () => {
    if (!name || variants.length < 2) {
      alert('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      await safeApi.post('/ab-tests', {
        name,
        message_type: messageType,
        success_metric: successMetric,
        min_sample_size: minSampleSize,
        variants: variants.map((v, i) => ({
          ...v,
          weight: Math.round(100 / variants.length)
        }))
      });
      onCreated();
    } catch (err) {
      console.error('Failed to create test:', err);
      alert('Failed to create test');
    } finally {
      setCreating(false);
    }
  };

  const addVariant = () => {
    if (variants.length >= 4) return;
    setVariants([...variants, { 
      name: `Variant ${String.fromCharCode(65 + variants.length)}`, 
      subject: '', 
      content: '' 
    }]);
  };

  const removeVariant = (index) => {
    if (variants.length <= 2) return;
    setVariants(variants.filter((_, i) => i !== index));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold">Create A/B Test</h2>
        </div>
        
        <div className="p-6 space-y-4">
          {/* Test Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Test Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Subject Line Test - Thank You"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Message Type & Metric */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message Type</label>
              <select
                value={messageType}
                onChange={(e) => setMessageType(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="thank_you">Thank You</option>
                <option value="review_request">Review Request</option>
                <option value="win_back">Win Back</option>
                <option value="upsell">Upsell</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Success Metric</label>
              <select
                value={successMetric}
                onChange={(e) => setSuccessMetric(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                {/* No Open Rate option: opens are untrusted (Apple MPP) —
                    a winner picked by opens can be picked by Apple's bots.
                    Existing tests created with open_rate still work. */}
                <option value="click_rate">Click Rate</option>
                <option value="conversion_rate">Conversion Rate</option>
                <option value="revenue">Revenue</option>
              </select>
            </div>
          </div>

          {/* Sample Size */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Minimum Sample Size (per variant)
            </label>
            <input
              type="number"
              value={minSampleSize}
              onChange={(e) => setMinSampleSize(parseInt(e.target.value) || 100)}
              min="50"
              max="10000"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Variants */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Variants</label>
              {variants.length < 4 && (
                <button
                  onClick={addVariant}
                  className="text-sm text-purple-600 hover:text-purple-700"
                >
                  + Add Variant
                </button>
              )}
            </div>
            
            <div className="space-y-3">
              {variants.map((variant, index) => (
                <div key={index} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <input
                      type="text"
                      value={variant.name}
                      onChange={(e) => {
                        const updated = [...variants];
                        updated[index].name = e.target.value;
                        setVariants(updated);
                      }}
                      className="font-medium bg-transparent border-b border-transparent hover:border-gray-300 focus:border-purple-500 focus:outline-none"
                    />
                    {variants.length > 2 && (
                      <button
                        onClick={() => removeVariant(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    value={variant.subject}
                    onChange={(e) => {
                      const updated = [...variants];
                      updated[index].subject = e.target.value;
                      setVariants(updated);
                    }}
                    placeholder="Subject line..."
                    className="w-full px-3 py-2 border rounded-lg mb-2 text-sm"
                  />
                  <textarea
                    value={variant.content}
                    onChange={(e) => {
                      const updated = [...variants];
                      updated[index].content = e.target.value;
                      setVariants(updated);
                    }}
                    placeholder="Message content..."
                    rows={3}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="p-6 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={creating || !name}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {creating ? 'Creating...' : 'Create Test'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TestDetailsModal({ test, onClose }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex items-center justify-between">
          <h2 className="text-xl font-bold">{test.name}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>
        
        <div className="p-6">
          {/* Detailed Results */}
          <div className="space-y-4">
            {test.variants?.map(variant => (
              <div 
                key={variant.variant_id}
                className={`p-4 rounded-lg border ${
                  test.winner?.variant_id === variant.variant_id 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold">
                    {variant.name}
                    {test.winner?.variant_id === variant.variant_id && ' 🏆 Winner'}
                  </h3>
                  <span className="text-sm text-gray-500">{variant.weight}% traffic</span>
                </div>
                
                <div className="grid grid-cols-5 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold">{variant.metrics?.sent || 0}</p>
                    <p className="text-xs text-gray-500">Sent</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{variant.metrics?.open_rate || 0}%</p>
                    <p className="text-xs text-gray-500">Open Rate</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{variant.metrics?.click_rate || 0}%</p>
                    <p className="text-xs text-gray-500">Click Rate</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{variant.metrics?.conversion_rate || 0}%</p>
                    <p className="text-xs text-gray-500">Conversions</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">${variant.metrics?.revenue_per_message || 0}</p>
                    <p className="text-xs text-gray-500">Rev/Msg</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Statistical Analysis */}
          {test.statistical_significance && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium mb-2">Statistical Analysis</h4>
              <p className="text-sm text-gray-600">{test.statistical_significance.message}</p>
              <p className="text-sm text-gray-500 mt-1">
                Confidence: {test.statistical_significance.confidence}%
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="bg-gray-50 rounded-lg p-4 text-center">
      <span className="text-2xl">{icon}</span>
      <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="animate-pulse space-y-4">
        <div className="h-6 bg-gray-200 rounded w-1/3"></div>
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-20 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onCreateTest }) {
  return (
    <div className="bg-white rounded-lg shadow p-8 text-center">
      <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <span className="text-3xl">🧪</span>
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">No A/B Tests Yet</h3>
      <p className="text-gray-600 mb-6">
        Create your first test to start optimizing your messages
      </p>
      <button
        onClick={onCreateTest}
        className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
      >
        Create First Test
      </button>
    </div>
  );
}

function ProGateMessage() {
  return (
    <div className="bg-white rounded-lg shadow p-8 text-center">
      <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <span className="text-3xl">🧪</span>
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">A/B Testing</h3>
      <p className="text-gray-600 mb-6">
        Test different message variants to find what works best.
      </p>
      <p className="text-sm text-purple-600">
        Upgrade to Pro plan to unlock this feature
      </p>
    </div>
  );
}